import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import CountdownTimer from "@/components/CountdownTimer";
import { formatBRL, statusColor, statusLabel } from "@/lib/constants";
import { toast } from "sonner";
import { Send, Star, Check, X, ArrowLeft, Sparkles, MapPin, Package, CreditCard, Clock, Zap, FileText } from "lucide-react";
import { logger } from "@/lib/logger";

export default function OrderDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [order, setOrder] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [rating, setRating] = useState({ stars: 5, comment: "" });
  const [showRating, setShowRating] = useState(false);
  const [alreadyRated, setAlreadyRated] = useState(false);
  const chatBottomRef = useRef(null);
  const wsRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/orders/${id}`);
      setOrder(data);
      if (data.accepted_by === user.user_id || data.buyer_id === user.user_id) {
        if (["accepted", "completed"].includes(data.status)) {
          const { data: msgs } = await api.get(`/orders/${id}/messages`);
          setMessages(msgs);
        }
      }
    } catch (err) {
      if (err.response?.status === 404) {
        toast.error("Pedido não encontrado");
        nav("/");
      }
    }
    setLoading(false);
  }, [id, user, nav]);

  useEffect(() => {
    load();
    // Poll order metadata (status/timer) every 4s, but chat comes via WS
    const iv = setInterval(load, 6000);
    return () => clearInterval(iv);
  }, [load]);

  // WebSocket real-time chat
  useEffect(() => {
    if (!order || !user) return;
    const isParty =
      order.accepted_by === user.user_id || order.buyer_id === user.user_id;
    if (!isParty || !["accepted", "completed"].includes(order.status)) return;

    const token = localStorage.getItem("rivendas_token") || "";
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    const wsUrl = backend.replace(/^http/, "ws") + `/api/ws/orders/${id}?token=${encodeURIComponent(token)}`;
    let ws;
    try {
      ws = new WebSocket(wsUrl);
    } catch (err) {
      logger.warn("ws connect failed", err);
      return;
    }
    ws.onmessage = (ev) => {
      try {
        const payload = JSON.parse(ev.data);
        if (payload.type === "message") {
          setMessages((prev) => [...prev, payload]);
        }
      } catch (err) {
        logger.warn("ws parse", err);
      }
    };
    ws.onerror = (e) => logger.warn("ws error", e);
    wsRef.current = ws;
    return () => {
      try { ws.close(); } catch (err) { logger.warn("ws close", err?.message); }
      wsRef.current = null;
    };
  }, [id, order?.status, order?.accepted_by, order?.buyer_id, user]);

  useEffect(() => {
    if (chatBottomRef.current) chatBottomRef.current.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  const accept = async () => {
    setActing(true);
    try {
      const { data } = await api.post(`/orders/${id}/accept`);
      setOrder(data);
      toast.success("Pedido aceito! Você tem 10 minutos para negociar.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha ao aceitar");
    }
    setActing(false);
  };

  const release = async () => {
    setActing(true);
    try {
      await api.post(`/orders/${id}/release`);
      toast.info("Pedido liberado para outros vendedores");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha ao liberar pedido");
    }
    setActing(false);
  };

  const complete = async () => {
    setActing(true);
    try {
      await api.post(`/orders/${id}/complete`);
      toast.success("Negociação concluída! Avalie a outra parte.");
      setShowRating(true);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha");
    }
    setActing(false);
  };

  const cancel = async () => {
    if (!confirm("Cancelar este pedido?")) return;
    setActing(true);
    try {
      await api.post(`/orders/${id}/cancel`);
      toast.info("Pedido cancelado");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha ao cancelar");
    }
    setActing(false);
  };

  const doBoost = async () => {
    if ((user.boost_credits || 0) < 1) {
      toast.info("Você não tem créditos de destaque. Vamos comprar?");
      nav("/pagamentos");
      return;
    }
    setActing(true);
    try {
      await api.post(`/orders/${id}/boost`);
      toast.success("Pedido destacado! 🚀");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha ao destacar");
    }
    setActing(false);
  };

  const sendMsg = async (e) => {
    e.preventDefault();
    const trimmed = text.trim();
    if (!trimmed) return;
    // Prefer WS if open
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      try {
        ws.send(JSON.stringify({ text: trimmed }));
        setText("");
        return;
      } catch (err) {
        logger.warn("ws send failed, falling back to REST", err);
      }
    }
    try {
      await api.post(`/orders/${id}/messages`, { text: trimmed });
      setText("");
      const { data: msgs } = await api.get(`/orders/${id}/messages`);
      setMessages(msgs);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha ao enviar mensagem");
    }
  };

  const submitRating = async () => {
    try {
      await api.post(`/orders/${id}/rate`, rating);
      toast.success("Avaliação enviada!");
      setShowRating(false);
      setAlreadyRated(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha ao avaliar");
    }
  };

  if (loading || !order) {
    return <div className="text-center py-16 text-muted-foreground">Carregando...</div>;
  }

  const isBuyer = order.buyer_id === user.user_id;
  const isSeller = order.accepted_by === user.user_id;
  const canChat = isBuyer || isSeller;
  const canAccept = user.role === "seller" && !isBuyer && order.status === "open";

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 pb-24">
      <button
        onClick={() => nav(-1)}
        className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4"
        data-testid="back-btn"
      >
        <ArrowLeft size={16} /> Voltar
      </button>

      {/* Header */}
      <div className="bg-card border border-border rounded-2xl p-5 md:p-6">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h1 className="font-heading font-black text-2xl md:text-3xl tracking-tight" data-testid="order-title-display">
              {order.title}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Publicado por <Link to={`/users/${order.buyer_id}`} className="hover:text-primary underline">{order.buyer_name}</Link>
            </p>
          </div>
          <span
            className={`shrink-0 text-[10px] uppercase tracking-wider font-bold border px-2 py-1 rounded-md ${statusColor(order.status)}`}
            data-testid="order-status"
          >
            {statusLabel(order.status)}
          </span>
        </div>

        {order.description && (
          <p className="text-sm text-muted-foreground mb-4">{order.description}</p>
        )}

        {order.photos && order.photos.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4" data-testid="order-photos">
            {order.photos.map((url, idx) => {
              const isPdf = url.toLowerCase().includes(".pdf") || url.includes("application/pdf");
              const fullUrl = url.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
              return isPdf ? (
                <a
                  key={url}
                  href={fullUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-20 h-20 rounded-lg bg-secondary border border-border flex flex-col items-center justify-center text-muted-foreground hover:border-primary"
                >
                  <FileText size={22} />
                  <span className="text-[10px] mt-1">PDF</span>
                </a>
              ) : (
                <a
                  key={url}
                  href={fullUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-20 h-20 rounded-lg overflow-hidden border border-border hover:border-primary"
                >
                  <img src={fullUrl} alt="" className="w-full h-full object-cover" />
                </a>
              );
            })}
          </div>
        )}

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            {order.order_type === "compra" && (
              <>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Valor máx.</p>
                <p className="font-mono font-black text-primary text-3xl mt-1" data-testid="order-max-price">
                  {formatBRL(order.max_price)}
                </p>
              </>
            )}
            {order.order_type === "permuta" && (
              <>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">🔄 Ofereço em troca</p>
                <p className="text-sm font-medium mt-1">{order.offered_item || "—"}</p>
              </>
            )}
            {order.order_type === "doacao" && (
              <>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">🎁 Doação</p>
                <p className="text-sm font-medium mt-1">
                  {order.donation_direction === "dar" ? "Ofereço para doar" : "Preciso receber doação"}
                </p>
              </>
            )}
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Categoria</p>
            <p className="font-medium mt-1">{order.category || "—"}</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {order.condition} · qtd {order.quantity}
            </p>
          </div>
        </div>

        {order.ai_suggested_price && (
          <div className="flex items-center gap-2 text-xs text-blue-500 mb-4 bg-blue-500/10 border border-blue-500/30 rounded-lg px-3 py-2">
            <Sparkles size={12} />
            <span>IA estimou preço médio: <strong className="font-mono">{formatBRL(order.ai_suggested_price)}</strong></span>
          </div>
        )}

        <div className="grid grid-cols-3 gap-2 text-xs text-muted-foreground border-t border-border pt-4">
          <div className="flex items-center gap-1"><MapPin size={12} /> {order.city}</div>
          <div className="flex items-center gap-1"><Package size={12} /> {order.max_distance_km} km</div>
          <div className="flex items-center gap-1"><CreditCard size={12} /> {order.payment_method}</div>
        </div>

        {/* Accept timer */}
        {order.status === "accepted" && order.accept_expires_at && (
          <div className="mt-4 p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-widest font-bold text-amber-500">Tempo restante</p>
              <p className="text-sm text-muted-foreground mt-1">
                {isSeller ? "Você aceitou este pedido. Responda antes que o timer expire." : `Aceito por ${order.accepted_by_name}`}
              </p>
            </div>
            <div className="text-3xl md:text-4xl">
              <CountdownTimer target={order.accept_expires_at} testId="detail-timer" />
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="mt-5 flex flex-col sm:flex-row gap-2">
          {canAccept && (
            <Button
              onClick={accept}
              disabled={acting}
              data-testid="accept-order-btn"
              className="flex-1 h-14 text-base font-black gap-2 cta-pulse"
            >
              <Check size={20} strokeWidth={3} /> Aceitar Pedido
            </Button>
          )}
          {isSeller && order.status === "accepted" && (
            <>
              <Button
                onClick={complete}
                disabled={acting}
                data-testid="complete-order-btn"
                className="flex-1 h-12 font-bold"
              >
                Concluir venda
              </Button>
              <Button
                onClick={release}
                disabled={acting}
                variant="outline"
                data-testid="release-order-btn"
                className="h-12"
              >
                Liberar
              </Button>
            </>
          )}
          {isBuyer && order.status === "accepted" && (
            <Button
              onClick={complete}
              disabled={acting}
              data-testid="buyer-complete-btn"
              className="flex-1 h-12 font-bold"
            >
              Confirmar recebimento e concluir
            </Button>
          )}
          {isBuyer && ["open", "accepted"].includes(order.status) && (
            <Button
              onClick={cancel}
              disabled={acting}
              variant="outline"
              data-testid="cancel-order-btn"
              className="h-12 gap-1 text-destructive hover:text-destructive"
            >
              <X size={16} /> Cancelar
            </Button>
          )}
          {isBuyer && order.status === "open" && !order.is_boosted && (
            <Button
              onClick={doBoost}
              disabled={acting}
              variant="outline"
              data-testid="boost-order-btn"
              className="h-12 gap-1 border-primary text-primary hover:bg-primary/10"
            >
              <Zap size={16} /> Destacar
            </Button>
          )}
          {order.status === "completed" && !alreadyRated && canChat && (
            <Button
              onClick={() => setShowRating(true)}
              data-testid="open-rate-btn"
              className="flex-1 h-12 gap-1 font-bold"
            >
              <Star size={16} /> Avaliar
            </Button>
          )}
        </div>
      </div>

      {/* Rating */}
      {showRating && (
        <div className="mt-4 bg-card border border-border rounded-2xl p-5" data-testid="rating-box">
          <h3 className="font-heading font-bold text-lg mb-3">Como foi sua experiência?</h3>
          <div className="flex gap-2 mb-3">
            {[1, 2, 3, 4, 5].map((s) => (
              <button
                key={s}
                data-testid={`rate-star-${s}`}
                onClick={() => setRating((r) => ({ ...r, stars: s }))}
                className="p-1"
              >
                <Star
                  size={32}
                  className={s <= rating.stars ? "text-amber-500 fill-amber-500" : "text-muted-foreground"}
                />
              </button>
            ))}
          </div>
          <Input
            data-testid="rate-comment"
            value={rating.comment}
            onChange={(e) => setRating((r) => ({ ...r, comment: e.target.value }))}
            placeholder="Comentário (opcional)"
            className="mb-3"
          />
          <div className="flex gap-2">
            <Button onClick={submitRating} className="flex-1 font-bold" data-testid="submit-rating">
              Enviar avaliação
            </Button>
            <Button onClick={() => setShowRating(false)} variant="outline">Cancelar</Button>
          </div>
        </div>
      )}

      {/* Chat */}
      {canChat && ["accepted", "completed"].includes(order.status) && (
        <div className="mt-4 bg-card border border-border rounded-2xl flex flex-col overflow-hidden">
          <div className="p-4 border-b border-border">
            <h3 className="font-heading font-bold flex items-center gap-2">
              <Clock size={16} /> Chat com {isBuyer ? order.accepted_by_name : order.buyer_name}
            </h3>
          </div>
          <div className="flex-1 max-h-96 overflow-y-auto p-4 space-y-3" data-testid="chat-list">
            {messages.length === 0 ? (
              <p className="text-center text-sm text-muted-foreground py-8">
                Envie a primeira mensagem
              </p>
            ) : (
              messages.map((m) => {
                const mine = m.sender_id === user.user_id;
                return (
                  <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                    <div
                      className={`max-w-[75%] rounded-2xl px-4 py-2 text-sm ${
                        mine
                          ? "bg-primary text-primary-foreground"
                          : "bg-secondary text-foreground"
                      }`}
                    >
                      {!mine && <p className="text-[10px] font-bold opacity-70 mb-0.5">{m.sender_name}</p>}
                      <p>{m.text}</p>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={chatBottomRef} />
          </div>
          {order.status === "accepted" && (
            <form onSubmit={sendMsg} className="p-3 border-t border-border flex gap-2">
              <Input
                data-testid="chat-input"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Digite sua mensagem..."
                className="flex-1"
              />
              <Button type="submit" data-testid="chat-send" className="gap-1">
                <Send size={16} />
              </Button>
            </form>
          )}
        </div>
      )}
    </div>
  );
}
